/**
 * Live Notification Service
 * Connects to the main backend via Socket.IO (same port as the REST API).
 * Used by TopBar and any other component that needs real-time alerts.
 */
import { io } from 'socket.io-client';
import { getNotifURL } from '../utils/api';
import AuthService from './authService';

const NOTIF_SERVER = getNotifURL();
const LOCAL_CACHE_PREFIX = 'kaaryaalaya:notifications:';
const SOCKET_IO_PATH = '/api/socket.io';
const PRESENCE_PING_INTERVAL_MS = 25 * 1000;
const PRESENCE_SESSION_STORAGE_KEY = 'ka:presence-session';
const PRESENCE_PING_URL = `${NOTIF_SERVER}/api/admin/staff-teamwork/presence/ping`;

let socket = null;
let presencePingTimer = null;

function createPresenceSessionId() {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `presence-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function getPresenceSessionId() {
  const fallbackId = createPresenceSessionId();

  if (typeof window === 'undefined' || !window.sessionStorage) {
    return fallbackId;
  }

  try {
    const existing = window.sessionStorage.getItem(PRESENCE_SESSION_STORAGE_KEY);
    if (existing) return existing;

    window.sessionStorage.setItem(PRESENCE_SESSION_STORAGE_KEY, fallbackId);
    return fallbackId;
  } catch {
    return fallbackId;
  }
}

function getPresenceRequestHeaders() {
  const apiKey = AuthService.getApiKey();
  if (!apiKey) return null;

  return {
    'Content-Type': 'application/json',
    'x-api-key': apiKey,
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  };
}

async function pingPresence() {
  const headers = getPresenceRequestHeaders();
  if (!headers) return;

  try {
    await fetch(PRESENCE_PING_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify({ presenceId: getPresenceSessionId() }),
      credentials: 'include',
    });
  } catch {
    // Ignore fallback heartbeat failures.
  }
}

async function clearPresence() {
  const headers = getPresenceRequestHeaders();
  if (!headers) return;

  try {
    await fetch(PRESENCE_PING_URL, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({ presenceId: getPresenceSessionId() }),
      credentials: 'include',
    });
  } catch {
    // Ignore fallback heartbeat failures.
  }
}

function startPresenceHeartbeat() {
  if (presencePingTimer) return;
  pingPresence();
  presencePingTimer = window.setInterval(pingPresence, PRESENCE_PING_INTERVAL_MS);
}

function stopPresenceHeartbeat() {
  if (!presencePingTimer) return;
  window.clearInterval(presencePingTimer);
  presencePingTimer = null;
}

function cacheKey(userId) {
  return `${LOCAL_CACHE_PREFIX}${String(userId)}`;
}

function isSameNotification(left, right) {
  if (!left || !right) return false;
  if (left.id === right.id) return true;
  return (
    left.title === right.title &&
    left.link === right.link &&
    (left.message || '') === (right.message || '')
  );
}

function mergeNotificationLists(existing, incoming) {
  const merged = [...existing];

  incoming.forEach((nextNotif) => {
    const index = merged.findIndex((current) => isSameNotification(current, nextNotif));
    if (index >= 0) {
      merged[index] = { ...merged[index], ...nextNotif };
      return;
    }
    merged.push(nextNotif);
  });

  return merged.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

function readLocalNotifications(userId) {
  if (!userId) return [];
  try {
    const raw = window.localStorage.getItem(cacheKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLocalNotifications(userId, notifications) {
  if (!userId) return;
  try {
    window.localStorage.setItem(cacheKey(userId), JSON.stringify(notifications));
  } catch {
    // Ignore storage failures
  }
}

function saveLocalNotification(userId, notification) {
  const cached = readLocalNotifications(userId);
  writeLocalNotifications(userId, mergeNotificationLists(cached, [notification]));
}

function markLocalNotificationRead(id, userId) {
  const cached = readLocalNotifications(userId).map((notif) => (
    notif.id === id ? { ...notif, isRead: true } : notif
  ));
  writeLocalNotifications(userId, cached);
}

function markAllLocalNotificationsRead(userId) {
  const cached = readLocalNotifications(userId).map((notif) => ({ ...notif, isRead: true }));
  writeLocalNotifications(userId, cached);
}

function deleteLocalNotification(id, userId) {
  const cached = readLocalNotifications(userId).filter((notif) => notif.id !== id);
  writeLocalNotifications(userId, cached);
}

function reconcileLocalNotifications(userId, serverNotifications) {
  const cached = readLocalNotifications(userId);
  const remainingLocal = cached.filter(
    (localNotif) => !serverNotifications.some((serverNotif) => isSameNotification(localNotif, serverNotif)),
  );
  writeLocalNotifications(userId, remainingLocal);
  return mergeNotificationLists(remainingLocal, serverNotifications);
}

/** Establish connection and join the user's private room. */
function connect(userId, userType = 'admin', userName = '', staffId = null) {
  startPresenceHeartbeat();
  if (socket && socket.connected) return;
  socket = io(NOTIF_SERVER, {
    path: SOCKET_IO_PATH,
    withCredentials: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 3000,
    transports: ['polling'],
    upgrade: false,
  });
  socket.on('connect', () => {
    socket.emit('join', {
      userId: String(userId),
      userType: String(userType || 'admin'),
      userName: String(userName || '').trim(),
      staffId: staffId == null ? null : String(staffId),
    });
  });
}

/** Tear down the socket (call on component unmount / logout). */
function disconnect() {
  stopPresenceHeartbeat();
  clearPresence();
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

/** Register a callback for incoming new notifications. */
function onNewNotification(cb) {
  if (!socket) return () => {};
  socket.on('new_notification', cb);
  return () => socket.off('new_notification', cb);
}

/** Register a callback for when a single notification is marked read (cross-tab sync). */
function onNotificationRead(cb) {
  if (!socket) return () => {};
  socket.on('notification_read', cb);
  return () => socket.off('notification_read', cb);
}

/** Register a callback for when all notifications are marked read (cross-tab sync). */
function onAllRead(cb) {
  if (!socket) return () => {};
  socket.on('all_read', cb);
  return () => socket.off('all_read', cb);
}

/** Register a callback for when a notification is deleted (cross-tab sync). */
function onNotificationDeleted(cb) {
  if (!socket) return () => {};
  socket.on('notification_deleted', cb);
  return () => socket.off('notification_deleted', cb);
}

function onTeamworkCountUpdated(cb) {
  if (!socket) return () => {};
  socket.on('teamwork_count_updated', cb);
  return () => socket.off('teamwork_count_updated', cb);
}

function onTeamworkMessageAlert(cb) {
  if (!socket) return () => {};
  socket.on('teamwork_message_alert', cb);
  return () => socket.off('teamwork_message_alert', cb);
}

function joinContactAdmin() {
  if (!socket) return;
  socket.emit('join_contact_admin');
}

function leaveContactAdmin() {
  if (!socket) return;
  socket.emit('leave_contact_admin');
}

function onContactAdminUpdate(cb) {
  if (!socket) return () => {};
  socket.on('contact_admin_update', cb);
  return () => socket.off('contact_admin_update', cb);
}

/** "Who's viewing this page" -- classroom weekly schedule. */
function joinScheduleViewers({ staffId, name, imagePath } = {}) {
  if (!socket) return;
  socket.emit('join_schedule_viewers', { staffId, name, imagePath });
}

function leaveScheduleViewers() {
  if (!socket) return;
  socket.emit('leave_schedule_viewers');
}

function onScheduleViewersUpdate(cb) {
  if (!socket) return () => {};
  socket.on('schedule_viewers_update', cb);
  return () => socket.off('schedule_viewers_update', cb);
}

/** Google-Sheets-style live refresh -- fires whenever anyone saves/clears/
 * duplicates a classroom schedule session, for everyone else currently
 * viewing the page. */
function onScheduleUpdated(cb) {
  if (!socket) return () => {};
  socket.on('schedule_updated', cb);
  return () => socket.off('schedule_updated', cb);
}

// ── REST helpers ──────────────────────────────────────────────────────────────

/** Fetch paginated notifications for a user. */
async function fetchNotifications(userId, { limit = 30, offset = 0 } = {}) {
  try {
    const res = await fetch(
      `${NOTIF_SERVER}/api/notifications?userId=${encodeURIComponent(userId)}&limit=${limit}&offset=${offset}`,
    );
    if (!res.ok) {
      return readLocalNotifications(userId);
    }
    const data = await res.json();
    return reconcileLocalNotifications(userId, data.notifications || []);
  } catch {
    return readLocalNotifications(userId);
  }
}

/** Mark a single notification as read. */
async function markRead(id, userId) {
  markLocalNotificationRead(id, userId);
  await fetch(`${NOTIF_SERVER}/api/notifications/${id}/read`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId: String(userId) }),
  });
}

/** Mark all notifications as read for a user. */
async function markAllRead(userId) {
  markAllLocalNotificationsRead(userId);
  await fetch(`${NOTIF_SERVER}/api/notifications/read-all?userId=${encodeURIComponent(userId)}`, {
    method: 'PATCH',
  });
}

/** Delete a notification. */
async function deleteNotification(id, userId) {
  deleteLocalNotification(id, userId);
  await fetch(`${NOTIF_SERVER}/api/notifications/${id}?userId=${encodeURIComponent(userId)}`, {
    method: 'DELETE',
  });
}

const NotificationService = {
  connect,
  disconnect,
  onNewNotification,
  onNotificationRead,
  onAllRead,
  onNotificationDeleted,
  onTeamworkCountUpdated,
  onTeamworkMessageAlert,
  joinContactAdmin,
  leaveContactAdmin,
  onContactAdminUpdate,
  joinScheduleViewers,
  leaveScheduleViewers,
  onScheduleViewersUpdate,
  onScheduleUpdated,
  fetchNotifications,
  markRead,
  markAllRead,
  deleteNotification,
  saveLocalNotification,
  readLocalNotifications,
  mergeNotificationLists,
};

export default NotificationService;
